import { useLocalization } from '@components/Localization'
import { Page, pickSeoProps } from '@components/Page'
import FeaturedSelection from '@components/_rolex/FeaturedSelection'
import Hero from '@components/_rolex/Hero'
import RolexPage from '@components/_rolex/Page'
import ProductCard from '@components/_rolex/ProductCard'
import KeepExploring from '@components/_rolex/Sliders/KeepExploring'
import TextBlock from '@components/_rolex/TextBlock'
import { Box, Container, Flex, Grid, Image } from '@gassan-ui'
import { getKontentImage, getPageByCodename, SingleItemResponse } from '@lib/kontent'
import { RolexHomepage as RolexHomepageModel } from '@lib/kontent/models/RolexHomepage'
import { Button, Heading, Text } from '@rolex-ui'
import { GetStaticProps } from 'next'
import { rem } from 'polished'
import React, { FC } from 'react'

type Props = SingleItemResponse<RolexHomepageModel>

const RolexHomepage: FC<Props> = ({ item, modular_content }) => {
  const { messages, lang } = useLocalization()
  const families = item.elements.families.value.map(key => modular_content[key].elements)
  const featuredItems = [
    {
      images: [item.elements.first_image_mobile, item.elements.first_image_desktop],
      title: modular_content[item.elements.first_link.value[0]].elements.title.value,
      href: modular_content[item.elements.first_link.value[0]].elements.url_slug.value
    },
    {
      images: [item.elements.second_image_mobile, item.elements.second_image_desktop],
      title: modular_content[item.elements.second_link.value[0]].elements.title.value,
      href: modular_content[item.elements.second_link.value[0]].elements.url_slug.value
    },
    {
      images: [item.elements.third_image_mobile, item.elements.third_image_desktop],
      title: modular_content[item.elements.third_link.value[0]].elements.title.value,
      href: modular_content[item.elements.third_link.value[0]].elements.url_slug.value
    }
  ]
  const keepExploringItems = item.elements.keep_exploring_slider_items.value.map(
    key => modular_content[key].elements
  )
  const sources = [
    getKontentImage(item.elements.rolex_hero__mobile?.value[0]?.url, { width: 640 }),
    getKontentImage(item.elements.rolex_hero__desktop?.value[0]?.url, { width: 1680 })
  ].filter(t => t)

  return (
    <Page {...pickSeoProps(item.elements)} slug={'rolex'}>
      <RolexPage>
        <Hero sources={sources} loading="eager" />
        <Box height={1} bg="shades.300" width="100%"></Box>
        <TextBlock
          preTitle={item.elements.watches_pre_title.value}
          title={item.elements.watches_title.value}
          description={item.elements.watches_body.value}
          pt={[30, 60]}
        />
        <Grid
          gridTemplateColumns={['repeat(2, 1fr)', 'repeat(3, 1fr)']}
          maxWidth={1020}
          mx="auto"
          mb="10vh"
        >
          {families &&
            families.map((item, key) => {
              // Check if urlslug has a query, if it does, append it to the href prop
              const query = item.url_slug.value.split('?')
              const append = query[1] ? `?${query[1]}` : ''
              return (
                <ProductCard
                  key={key}
                  href={`/${lang}/rolex/${item.url_slug.value}${append}`}
                  sources={[getKontentImage(item.image?.value[0]?.url, { width: 400 })].filter(
                    t => t
                  )}
                  title={item.title.value}
                />
              )
            })}
        </Grid>
        <FeaturedSelection
          items={featuredItems}
          title={item.elements.featured_selections_title.value}
        />
        <Container maxWidth={rem(650)} mb={{ _: '3rem', small: '6rem', large: '7.5rem' }}>
          <Flex
            alignItems="center"
            flexDirection="column"
            maxWidth="52rem"
            margin="0 auto"
            py={[4, 4, 5]}
            textAlign="center"
          >
            <Box p={['0.5rem 0 3rem']} width="100%">
              <Image
                ratio={0.55}
                sources={[
                  getKontentImage(item.elements.visit_store_image?.value[0]?.url, { width: 700 })
                ]}
                placeholderSrc={getKontentImage(item.elements.visit_store_image?.value[0]?.url, {
                  width: 50,
                  height: 50
                })}
              />
            </Box>
            <Heading variant="largeTitle" as="h2" mb={rem(10)}>
              {item.elements.visit_store_title.value}
            </Heading>
            <Text size="small" maxWidth="870px" mx="auto" mb={rem(20)}>
              {item.elements.visit_store_body.value}
            </Text>
            <Button as="link" href={`/${lang}/rolex/contact`}>
              {messages.rolex.navigation.contactUs}
            </Button>
          </Flex>
        </Container>
        <KeepExploring
          items={keepExploringItems}
          title={item.elements.keep_exploring_title.value}
        />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  try {
    const langParam =
      params && params.lang
        ? typeof params.lang === 'string'
          ? params.lang
          : params.lang.pop()
        : 'nl'
    const language = langParam === 'sg' ? 'en' : langParam
    const content = await getPageByCodename<RolexHomepageModel>({
      codename: 'rolex_homepage',
      lang: language,
      preview
    })

    return {
      props: {
        ...content
      }
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = () => {
  return {
    fallback: false,
    paths: [{ params: { lang: 'nl' } }, { params: { lang: 'en' } }, { params: { lang: 'sg' } }]
  }
}

export default RolexHomepage
